/**
 * Service for interacting with the local file system via the Node.js backend
 */
import axios from 'axios';

// Use environment variable for API base URL, fallback to domain or local
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 
                    (window.location.hostname === 'iburrow.com' ? 'http://iburrow.com:2401' : 'http://localhost:2401');
console.log('API_BASE_URL:', API_BASE_URL);

// Helper function to handle errors
const handleError = (error) => {
  console.error('API Error:', error);
  return {
    success: false,
    error: error.response?.data?.error || error.message
  };
};

const getLocalFilesForComparison = async (directoryPath = null) => {
  try {
    // Use the passed directory path or fall back to the workspace path from the API
    const path = directoryPath || '';
    console.log("Getting local files for comparison from path:", path);
    
    // Call backend API to get local files
    const response = await axios.get(`${API_BASE_URL}/api/workspace/files`, {
      params: { path }
    });
    
    if (!response.data || !response.data.success) {
      console.warn("Backend returned unsuccessful response:", response.data);
      // Fall back to mock data if the backend call fails
      return {
        success: false,
        error: response.data?.error || "Failed to fetch local files",
        files: []
      };
    }
    
    return {
      success: true,
      files: response.data.files || [],
      path: response.data.path || path
    };
    
  } catch (error) {
    console.error("Error getting local files for comparison:", error);
    return {
      success: false,
      error: error.message,
      files: []
    };
  }
};

export const FileSystemService = {
  /**
   * Get all available categories from workspace
   */
  getCategories: async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/categories`);
      return {
        success: true,
        categories: response.data.categories
      };
    } catch (error) {
      return handleError(error);
    }
  },
  
  /**
   * Get videos within a specific category
   * @param {string} category - Category path/folder name
   */
  getCategoryVideos: async (category) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/category/${encodeURIComponent(category)}/videos`);
      return {
        success: true,
        videos: response.data.videos
      };
    } catch (error) {
      return handleError(error);
    }
  },
  
  /**
   * Scan workspace and generate a temporary manifest
   */
  scanWorkspace: async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/workspace/scan`);
      return {
        success: true,
        movies: response.data.movies,
        manifestPath: response.data.manifestPath
      };
    } catch (error) {
      return handleError(error);
    }
  },
  
  /**
   * Get list of available manifests
   */
  getManifests: async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/manifests`);
      return {
        success: true,
        manifests: response.data.manifests
      };
    } catch (error) {
      return handleError(error);
    }
  },
  
  /**
 * Get specific manifest by name
 * @param {string} name - Manifest file name
 */
getManifest: async (manifestName) => {
  console.log(`FileSystemService: Fetching manifest ${manifestName}`);
  try {
    // Use API_BASE_URL for Linux compatibility
    const response = await axios.get(`${API_BASE_URL}/api/manifests/${manifestName}`);
    const data = response.data;
    
    if (data && data.manifest) {
      console.log(`FileSystemService: Successfully loaded manifest with ${data.manifest.movies?.length || 0} movies`);
      return {
        success: true,
        manifest: data.manifest
      };
    } else {
      console.error('FileSystemService: Invalid manifest data');
      return {
        success: false,
        error: 'Invalid manifest data structure'
      };
    }
  } catch (error) {
    console.error('FileSystemService: Error fetching manifest:', error);
    return {
      success: false,
      error: error.message
      };
    }
  },
  
  /**
   * Generate a thumbnail from a video at specific timestamp
   * @param {string} videoPath - Full path to video file
   * @param {number} timestamp - Timestamp in seconds
   */
  generateThumbnail: async (videoPath, timestamp = 0) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/api/videos/thumbnail`, {
        videoPath,
        timestamp
      });
      return {
        success: true,
        thumbnailPath: response.data.thumbnailPath
      };
    } catch (error) {
      return handleError(error);
    }
  },
  
  /**
   * Update video metadata
   * @param {string} videoId - Video ID/folder name
   * @param {string} categoryPath - Category path/folder name
   * @param {object} metadata - Metadata object to save
   */
  updateVideoMetadata: async (videoId, categoryPath, metadata) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/api/videos/metadata`, {
        videoId,
        categoryPath,
        metadata
      });
      return {
        success: true,
        message: response.data.message
      };
    } catch (error) {
      return handleError(error);
    }
  },
  
  /**
   * Create a new manifest file from the workspace
   */
  createManifest: async () => {
    try {
      const response = await axios.post(`${API_BASE_URL}/api/manifests/create`);
      return {
        success: true,
        manifestName: response.data.manifestName,
        manifestPath: response.data.manifestPath
      };
    } catch (error) {
      return handleError(error);
    }
  },
  
  /**
   * Create a new enhanced manifest file using Bunny.net data
   */
  createEnhancedManifest: async () => {
    try {
      console.log("Calling backend to create enhanced manifest using Bunny.net data");
      const response = await axios.post(`${API_BASE_URL}/api/manifests/create-enhanced`);
      
      return {
        success: true,
        manifestName: response.data.manifestName,
        manifestPath: response.data.manifestPath,
        stats: response.data.stats
      };
    } catch (error) {
      console.error("Error creating enhanced manifest:", error);
      return handleError(error);
    }
  },
  
  /**
   * Create categorized manifests (clips, shorts, films) based on video duration
   * @param {string} sourceManifestName - Name of the source manifest file
   */
  createCategorizedManifests: async (sourceManifestName) => {
    try {
      console.log(`FileSystemService: Creating categorized manifests from source: ${sourceManifestName}`);
      
      const response = await axios.post(`${API_BASE_URL}/api/manifests/create-categorized`, {
        sourceManifestName
      });
      
      console.log(`FileSystemService: Successfully created categorized manifests`);
      return {
        success: true,
        manifests: response.data.manifests,
        message: response.data.message
      };
    } catch (error) {
      console.error("Error creating categorized manifests:", error);
      return handleError(error);
    }
  },
  
  /**
   * Get URL for accessing a file via the backend
   * @param {string} filePath - Path relative to workspace base path
   */
  getFileUrl: (filePath) => {
    if (!filePath) return '';
    if (filePath.startsWith('http')) {
      return filePath; // Already a URL, don't modify
    }
    // Use API_BASE_URL for Linux/WSL compatibility
    return `${API_BASE_URL.replace(/\/api$/, '')}/files/${encodeURIComponent(filePath)}`;
  },
  
  /**
   * Transform local file paths in the movies array to URLs
   * @param {Array} movies - Array of movie objects
   */
  transformLocalPaths: (movies) => {
    if (!Array.isArray(movies)) {
      console.warn('transformLocalPaths received non-array:', movies);
      return [];
    }
    
    console.log(`Transforming ${movies.length} movies`);
    
    return movies.map(movie => {
      if (!movie) return movie;
      
      // Create a copy to avoid modifying the original
      const transformed = { ...movie };
      
      // Log for debugging
      console.log(`Processing movie: ${transformed.title}`);
      
      // Process video URL 
      if (transformed.videoUrl && typeof transformed.videoUrl === 'string') {
        // Normalize path separators
        transformed.videoUrl = transformed.videoUrl.replace(/\\/g, '/');
        console.log(`Processed videoUrl: ${transformed.videoUrl}`);
      }
      
      // Process thumbnail URL
      if (transformed.thumbnailUrl && typeof transformed.thumbnailUrl === 'string') {
        // Normalize path separators
        transformed.thumbnailUrl = transformed.thumbnailUrl.replace(/\\/g, '/');
        console.log(`Processed thumbnailUrl: ${transformed.thumbnailUrl}`);
      }
      
      return transformed;
    });
  },
  
  /**
   * Get all local files for comparison with Bunny.net
   */
  getLocalFilesForComparison,
  
  /**
   * Compare local files with Bunny.net
   */
  compareBunnynetFiles: async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/bunny/compare`);
      return {
        success: true,
        localFiles: response.data.localFiles,
        remoteVideos: response.data.remoteVideos,
        remoteFiles: response.data.remoteFiles,
        analysis: response.data.analysis
      };
    } catch (error) {
      return handleError(error);
    }
  }
};

export default FileSystemService;
